import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  MappingCell,
  DirectCell,
  YesNoCell,
  FloatCell,
  CellFunction,
  NoneGuard
} from '../../cells';
import { IEpxWellMgmt } from '../../../../../libs/apps/epxwellmgmt';

const DietAdherence = {
  [1]: '25%',
  [2]: '50%',
  [3]: '75%',
  [4]: '100%',
  [5]: 'Never'
};

const ExerciseAdherence = {
  [1]: '1 - 2',
  [2]: '3 - 5',
  [3]: 'None'
};

const StepRange = {
  [1]: '> 10,000',
  [2]: '7500 - 10,000',
  [3]: '5000 - 7500',
  [4]: '< 5000'
};

const CigaretteUsage = {
  [1]: 'None',
  [2]: '< 10',
  [3]: '10 - 20',
  [4]: '> 20'
};

interface Dependencies {
  askDiet: boolean;
  hasDietPlan: boolean;
  trackDietDays: boolean;
  askExercise: boolean;
  trackExerciseMinutes: boolean;
  hasStepDevice: boolean;
  hasScale: boolean;
  isSmoker: boolean;
  hasHealthCoach: boolean;
  askBloodSugar: boolean;
  askBloodPressure: boolean;
}

const FLAGS = [
  'askDiet',
  'hasDietPlan',
  'trackDietDays',
  'askExercise',
  'trackExerciseMinutes',
  'hasStepDevice',
  'hasScale',
  'isSmoker',
  'hasHealthCoach',
  'askBloodSugar',
  'askBloodPressure'
];

const getDependencies = (sessions: IEpxWellMgmt[]): Dependencies => {
  const deps: Dependencies = {
    askDiet: true,
    hasDietPlan: false,
    trackDietDays: false,
    askExercise: true,
    trackExerciseMinutes: false,
    hasStepDevice: false,
    hasScale: false,
    isSmoker: false,
    hasHealthCoach: false,
    askBloodSugar: false,
    askBloodPressure: false
  };
  for (const session of sessions) {
    if (session.schedule) {
      for (const flag of FLAGS) {
        if (session.schedule[flag] != null) {
          deps[flag] = session.schedule[flag];
        }
      }
      break;
    }
  }
  for (const session of sessions) {
    for (const flag of FLAGS) {
      if (session[flag]) {
        deps[flag] = true;
      }
    }
  }
  return deps;
};

const getFlattenedDependencies = (sessions: IEpxWellMgmt[]): boolean[] => {
  const deps = getDependencies(sessions);
  const _deps: boolean[] = [];
  for (const flag of FLAGS) {
    _deps.push(deps[flag]);
  }
  return _deps;
};

const BloodPressureCell: CellFunction<IEpxWellMgmt> = ({
  row: { original: session }
}) => {
  const systolic = session.systolicBP as number;
  const diastolic = session.diastolicBP as number;
  return (
    NoneGuard(systolic, diastolic) || (
      <span>
        {systolic || '?'}/{diastolic || '?'}
      </span>
    )
  );
};

const getColumns = (sessions: IEpxWellMgmt[]): Column<IEpxWellMgmt>[] => {
  const deps = getDependencies(sessions);
  const columns: Column<IEpxWellMgmt>[] = [];
  if (deps.askDiet) {
    if (deps.hasDietPlan) {
      if (deps.trackDietDays) {
        columns.push({
          Header: 'Diet Days',
          id: 'dietDays',
          Cell: DirectCell('dietDays') as CellFunction<IEpxWellMgmt>
        });
      } else {
        columns.push({
          Header: 'Diet Adherence',
          id: 'dietAdherence',
          Cell: ResponseGuard(
            MappingCell(
              'dietAdherence',
              DietAdherence
            ) as CellFunction<IEpxWellMgmt>
          )
        });
      }
    } else {
      columns.push({
        Header: 'Meals w/ Vegetables',
        id: 'vegetableMeals',
        Cell: DirectCell('vegetableMeals') as CellFunction<IEpxWellMgmt>
      });
    }
  }
  if (deps.askExercise) {
    if (deps.trackExerciseMinutes) {
      columns.push({
        Header: 'Exercise Minutes',
        id: 'exerciseMinutes',
        Cell: DirectCell('exerciseMinutes') as CellFunction<IEpxWellMgmt>
      });
    } else {
      columns.push({
        Header: 'Exercise Adherence',
        id: 'exerciseAdherence',
        Cell: ResponseGuard(
          MappingCell(
            'exerciseAdherence',
            ExerciseAdherence
          ) as CellFunction<IEpxWellMgmt>
        )
      });
    }
    if (deps.hasStepDevice) {
      columns.push({
        Header: 'Steps',
        id: 'stepRange',
        Cell: ResponseGuard(
          MappingCell('stepRange', StepRange) as CellFunction<IEpxWellMgmt>
        )
      });
    }
  }
  if (deps.hasScale) {
    columns.push({
      Header: 'Weight (lbs)',
      id: 'weight',
      Cell: ResponseGuard(FloatCell('weight', 2) as CellFunction<IEpxWellMgmt>)
    });
  }
  if (deps.isSmoker) {
    columns.push({
      Header: 'Cigarettes',
      id: 'cigaretteUsage',
      Cell: ResponseGuard(
        MappingCell(
          'cigaretteUsage',
          CigaretteUsage
        ) as CellFunction<IEpxWellMgmt>
      )
    });
  }
  if (deps.hasHealthCoach) {
    columns.push({
      Header: 'Want to Talk',
      id: 'requestConsult',
      Cell: ResponseGuard(
        YesNoCell('requestConsult') as CellFunction<IEpxWellMgmt>
      )
    });
  }
  if (deps.askBloodSugar) {
    columns.push(
      {
        Header: 'Blood Sugar',
        id: 'bloodSugar',
        Cell: ResponseGuard(
          DirectCell('bloodSugar') as CellFunction<IEpxWellMgmt>
        )
      },
      {
        Header: 'Has Eaten',
        accessor: 'hasEaten',
        Cell: ResponseGuard(YesNoCell('hasEaten') as CellFunction<IEpxWellMgmt>)
      }
    );
  }
  if (deps.askBloodPressure) {
    columns.push({
      Header: 'Blood Pressure',
      id: 'bp',
      Cell: ResponseGuard(BloodPressureCell)
    });
  }
  return columns;
};

const EpxWellMgmtTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxWellMgmt>
      url="/epxwellmgmt/data/"
      getColumns={getColumns}
      getDependencies={getFlattenedDependencies}
      {...props}
    />
  );
};

export default EpxWellMgmtTable;
